import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { DropdownSelector } from '@bingads-webui-react/dropdown-selector';
import { localizeSegmentationValue, segmentationType } from '@bingads-webui-campaign/segmentation-utilities';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';

import { METRIC_PREFERENCE_KEY_NAME, CAMPAIGN_TYPES } from '@bingads-webui-campaign/competition-auction-insight-preferences';

import {
  METRICS,
  getLocalizedMetrics,
  AdvisorChartWithMetrics,
  EmptyChart,
} from '@bingads-webui-campaign-react/advisor-chart';
import { getGeneralInsightsPreferences, saveGeneralInsightsPreferences, getPillControlTypeFromPreferences } from '@bingads-webui-campaign-react/competition-core';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/competition-tab');
const preferencesService = globalContext.requireDefault('component/preference/service/index');
const appConfig = globalContext.require('component/config/index');
const permissions = appConfig.get('Permissions');

const DEVICE_TYPE = {
  PC: 'PC',
  SmartPhone: 'SmartPhone',
  Tablet: 'Tablet',
};

function localizeDeviceType(value) {
  return localizeSegmentationValue({
    i18n,
    type: segmentationType.DeviceType,
    value,
  });
}

function getDeviceTypeList(raw, primaryDeviceType) {
  // get segmentData from first item
  const segmentData = raw[0].PerformanceMetrics.SegmentedData;

  if (_.size(segmentData) > 0 && _.size(segmentData[0].Key) > 1) {
    const keys = _.pluck(segmentData[0].Key, 'StringValue');

    if (_.intersection(keys, DEVICE_TYPE).length > 0) {
      // Move the primaryDeviceType to start of the list
      return _.union(
        [primaryDeviceType],
        _.values(DEVICE_TYPE)
      );
    }
  }

  return [null];
}

export class GeneralInsightsChartContainer extends React.PureComponent {
  static defaultProps = {
    domains: null,
    isShoppingCampaignType: false,
    parentPerfMarker: null,
    perfMarker: null,
  };

  static propTypes = {
    data: PropTypes.shape({
      totalCount: PropTypes.number.isRequired,
      items: PropTypes.arrayOf(PropTypes.shape({
        PerformanceMetrics: PropTypes.object.isRequired,
        DisplayDomain: PropTypes.string.isRequired,
      })).isRequired,
    }).isRequired,
    domains: PropTypes.arrayOf(PropTypes.string),
    isShoppingCampaignType: PropTypes.bool,
    /* eslint-disable react/forbid-prop-types */
    currentScenario: PropTypes.object.isRequired,
    currentActivity: PropTypes.object.isRequired,
    parentPerfMarker: perfMarkerPropTypes,
    perfMarker: perfMarkerPropTypes,
  };

  constructor(props) {
    super(props);
    this.pillControlType = getPillControlTypeFromPreferences(preferencesService);

    this.mountChartActitivy = props.currentScenario.mountChart.create();
    this.mountChartActitivy.start();
    const preferences = getGeneralInsightsPreferences(preferencesService);
    const { primaryDeviceType } = preferences;

    this.metrics = getLocalizedMetrics(
      i18n, props.isShoppingCampaignType,
      permissions.IsAvgPositionDeprecationPhase1Enabled,
      permissions.IsAvgPositionDeprecationPhase2Enabled
    );

    // since when campaign type is changed, general insights component will be re-constructed,
    // here we can store a value which is computed from prop.
    if (props.isShoppingCampaignType) {
      this.metricPreferenceName = METRIC_PREFERENCE_KEY_NAME[CAMPAIGN_TYPES.Shopping];
    } else {
      this.metricPreferenceName = METRIC_PREFERENCE_KEY_NAME[CAMPAIGN_TYPES.Search];
    }

    let selectedMetric = preferences[this.metricPreferenceName] || METRICS.ImpressionShare;
    const availableMetrics = _.map(this.metrics, item => item.metric);

    if (!_.contains(availableMetrics, selectedMetric)) {
      selectedMetric = METRICS.ImpressionShare;

      _.defer(() => {
        saveGeneralInsightsPreferences({
          [this.metricPreferenceName]: selectedMetric,
        }, preferencesService);
      });
    }

    this.state = {
      selectedMetric,
      primaryDeviceType: primaryDeviceType || DEVICE_TYPE.PC,
    };

    this.deviceTypes = _.map(_.keys(DEVICE_TYPE), (deviceType, id) => ({
      id,
      value: localizeDeviceType(deviceType),
      deviceType,
    }));
  }

  componentDidMount() {
    const { totalCount } = this.props.data;
    this.mountChartActitivy.stop();
    if (totalCount === 0) {
      this.onChartRenderedCallback();
    }
  }

  componentWillReceiveProps() {
    const prevPillControlType = this.pillControlType;
    this.pillControlType = getPillControlTypeFromPreferences(preferencesService);
    if (this.props.parentPerfMarker && (this.pillControlType !== prevPillControlType)) {
      this.actionPerfMarker = this.props.parentPerfMarker.createChild(`Auction_Insight_Chart_${this.pillControlType}_Action`);
      this.actionPerfMarker.willInit();
    }
    return null;
  }

  onMetricChange = ({ metric }) => {
    if (this.props.parentPerfMarker) {
      this.actionPerfMarker = this.props.parentPerfMarker.createChild(`Auction_Insight_Chart_${this.pillControlType}_Action`);
      this.actionPerfMarker.willInit();
    }

    saveGeneralInsightsPreferences({ [this.metricPreferenceName]: metric }, preferencesService);
    this.setState({
      selectedMetric: metric,
    });
  };

  onDeviceTypeChange = ({ deviceType }) => {
    if (this.props.parentPerfMarker) {
      this.actionPerfMarker = this.props.parentPerfMarker.createChild(`Auction_Insight_Chart_${this.pillControlType}_Action`);
      this.actionPerfMarker.willInit();
    }

    saveGeneralInsightsPreferences({ primaryDeviceType: deviceType }, preferencesService);
    this.setState({
      primaryDeviceType: deviceType,
    });
  };

  onChartRenderedCallback = () => {
    if (this.props.perfMarker && !this.props.perfMarker.isDone) {
      this.props.perfMarker.done();
    }
    if (this.actionPerfMarker && !this.actionPerfMarker.isDone) {
      this.actionPerfMarker.done();
    }
  };

  render() {
    const { totalCount, items } = this.props.data;

    if (totalCount > 0) {
      const { primaryDeviceType, selectedMetric } = this.state;
      const deviceTypeList = getDeviceTypeList(items, primaryDeviceType);
      const showDeviceTypeDropdown = _.size(deviceTypeList) > 1;


      const selectedMetricItem = _.findWhere(this.metrics, { metric: selectedMetric });
      const selectedDeviceType = _.findWhere(this.deviceTypes, { deviceType: primaryDeviceType });

      this.props.currentActivity.trace(
        `selected metric is ${selectedMetric}, selected device type is ${primaryDeviceType}`,
        'GeneralInsightsChart/render'
      );

      return (
        <div className="general-insights-chart-container">
          <div className="chart-topbar row">
            <div className="col-sm-12">
              <div className="primary-metric dropdown">
                <DropdownSelector
                  selectedItem={selectedMetricItem}
                  onSelect={this.onMetricChange}
                  totalCount={_.size(this.metrics)}
                  items={this.metrics}
                  i18n={i18n}
                  enableArrowKeysSupport
                  shouldFocusFirstFocusableItemOnMount
                />
              </div>
              {showDeviceTypeDropdown &&
                <div className="device-type dropdown">
                  <DropdownSelector
                    selectedItem={selectedDeviceType}
                    onSelect={this.onDeviceTypeChange}
                    totalCount={_.size(this.deviceTypes)}
                    items={this.deviceTypes}
                    i18n={i18n}
                    enableArrowKeysSupport
                    shouldFocusFirstFocusableItemOnMount
                  />
                </div>}
            </div>
          </div>
          <AdvisorChartWithMetrics
            i18n={i18n}
            selectedMetric={selectedMetric}
            primaryDeviceType={primaryDeviceType}
            domains={this.props.domains}
            items={items}
            onChartRenderedCallback={this.onChartRenderedCallback}
          />
        </div >
      );
    }

    return (<EmptyChart i18n={i18n} />);
  }
}
